import { useState } from 'react';

import type { PettyCashCount, PettyCashEntry } from '@dcc-erp/shared';

import {
  usePettyCashEntryAttachments,
  useRealizePettyCashEntryVat,
} from '../../lib/queries/petty-cash';
import { Can } from '../Can';

// M3 maker-checker — the engine columns (approvalStatus / currentStep /
// submittedBy / submittedAt / approvedBy / approvedAt / rejectedReason /
// returnedReason) and the materialized approvals[] chain are now ALL declared on
// the canonical PettyCashEntry Zod type (listEntries hydrates them from the DB),
// so the approval panel reads them directly — no local field augmentation needed.
export type EntryWithApproval = PettyCashEntry;

export type BranchStatus = 'submitted' | 'pending' | 'approved' | 'reimbursed' | 'overdue';

export interface Branch {
  id: string;
  floatId: string;
  branchUuid: string;
  name: string;
  entries: number;
  total: number;
  float: number;
  used: number;
  remain: number;
  usedPct: number;
  status: BranchStatus;
  lastSub: string;
  custodian: string;
}

export interface Aggregate {
  total: number;
  entries: number;
  float: number;
  used: number;
  pending: number;
  submitted: number;
  approved: number;
  reimbursed: number;
  overdue: number;
}

// An entry is engine-managed (has an approval lifecycle to surface) when the
// accounting status is PENDING_APPROVAL/REJECTED OR it carries a non-null
// approvalStatus. Mirrors the AP "row carries approvalStatus" trigger.
export function isEngineManaged(e: EntryWithApproval): boolean {
  return (
    e.status === 'PENDING_APPROVAL' ||
    e.status === 'REJECTED' ||
    (e.approvalStatus != null && e.approvalStatus !== '')
  );
}

// Deterministic palette for category dots — indexed by row position so any
// real 8-digit expense code gets a colour regardless of its numeric value.
export const PALETTE = [
  '#b54708',
  '#1a56a6',
  '#117a3d',
  '#b42318',
  '#7030A0',
  '#0e7490',
  '#a16207',
  '#3f3f46',
  '#52525b',
  '#0369a1',
] as const;

export const SEARCH_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")";

// Today as 'YYYY-MM-DD' (no TZ-shift surprises since we strip the time). Used
// as default for date inputs in the entry/replenish modals.
export function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Round to 2 dp, half-up — matches the server's Prisma Decimal.toDecimalPlaces(2)
// (ROUND_HALF_UP) so a web-derived WHT amount agrees with the server recompute.
export function round2(n: number): number {
  const sign = n < 0 ? -1 : 1;
  const cents = Math.floor(Math.abs(n) * 100 + 0.5 + 1e-9);
  return (sign * cents) / 100;
}

// ─── Receipt badge — 3-state ────────────────────────────────────────────────
// hasReceipt      → nothing shown (receipt attached, all good)
// !hasReceipt && isNoDoc  → neutral "ไม่มีเอกสาร" (deliberate no-doc)
// !hasReceipt && !isNoDoc → negative "ไม่มีใบเสร็จ" (missing attachment)
export function ReceiptBadge({ hasReceipt, isNoDoc }: { hasReceipt: boolean; isNoDoc: boolean }) {
  if (hasReceipt) return null;
  if (isNoDoc)
    return (
      <span className="badge neutral" style={{ fontSize: 12, marginLeft: 4 }}>
        <span className="bdot" />
        ไม่มีเอกสาร
      </span>
    );
  return (
    <span className="badge negative" style={{ fontSize: 12, marginLeft: 4 }}>
      <span className="bdot" />
      ไม่มีใบเสร็จ
    </span>
  );
}

// ─── Approval-status badge — surfaces the engine state of a PENDING_APPROVAL ──
// entry (DRAFT/PENDING/RETURNED/APPROVED/REJECTED). Falls back to a neutral
// "รออนุมัติ" when approvalStatus is null on a PENDING_APPROVAL row.
export function ApprovalStatusBadge({
  approvalStatus,
}: {
  approvalStatus: string | null | undefined;
}) {
  switch (approvalStatus) {
    case 'DRAFT':
      return (
        <span className="badge neutral">
          <span className="bdot" />
          ร่าง
        </span>
      );
    case 'RETURNED':
      return (
        <span className="badge warning">
          <span className="bdot" />
          ส่งกลับแก้ไข
        </span>
      );
    case 'REJECTED':
      return (
        <span className="badge negative">
          <span className="bdot" />
          ไม่อนุมัติ
        </span>
      );
    case 'APPROVED':
      return (
        <span className="badge positive">
          <span className="bdot" />
          อนุมัติแล้ว
        </span>
      );
    case 'PENDING':
    default:
      return (
        <span className="badge warning">
          <span className="bdot" />
          รออนุมัติ
        </span>
      );
  }
}

// ─── Count / Close status badge — engine-driven (PCC / PCX) ──────────────────
// PENDING_APPROVAL routes through ApprovalStatusBadge (DRAFT/RETURNED/PENDING/
// REJECTED/APPROVED); POSTED + REJECTED terminals get a flat badge.
export function CountCloseStatusBadge({
  status,
  approvalStatus,
}: {
  status: PettyCashCount['status'];
  approvalStatus: string | null | undefined;
}) {
  if (status === 'POSTED')
    return (
      <span className="badge positive">
        <span className="bdot" />
        ลงบัญชีแล้ว
      </span>
    );
  if (status === 'REJECTED')
    return (
      <span className="badge negative">
        <span className="bdot" />
        ไม่อนุมัติ
      </span>
    );
  return <ApprovalStatusBadge approvalStatus={approvalStatus} />;
}

// Difference colour cue: over (>0) positive, short (<0) negative, exact neutral.
export function diffColor(diff: number): string | undefined {
  if (diff > 0) return 'var(--positive)';
  if (diff < 0) return 'var(--negative)';
  return undefined;
}

// ─── View-receipt link — lazily fetches presigned URL on click ───────────────
// Uses refetch() promise so we never put setState inside useEffect.
export function ReceiptViewLink({
  companyId,
  entryId,
}: {
  companyId: string | undefined;
  entryId: string;
}) {
  const [loading, setLoading] = useState(false);
  // enabled:false — do NOT auto-fetch per row; only fetch on click via refetch().
  const { refetch } = usePettyCashEntryAttachments(companyId, entryId, { enabled: false });

  function handleClick() {
    if (loading) return;
    setLoading(true);
    void refetch()
      .then((result) => {
        const url = result.data?.[0]?.downloadUrl;
        if (url) window.open(url, '_blank', 'noopener');
      })
      .finally(() => setLoading(false));
  }

  return (
    <button
      type="button"
      className="btn sm ghost"
      onClick={handleClick}
      disabled={loading}
      style={{ fontSize: 12, height: 22, padding: '0 6px', marginLeft: 4 }}
      title="ดูใบเสร็จ"
    >
      {loading ? '…' : 'ดูใบเสร็จ'}
    </button>
  );
}

// ─── Realize-input-VAT action — SUSPENSE entry whose tax invoice has cleared ──
// Surfaced only when vatTreatment === 'SUSPENSE' && !vatRealizedAt. Gated on
// pettycash.create (per M4 spec). Posts the suspense→claimable JV server-side.
export function PCRealizeVatAction({
  companyId,
  entry,
}: {
  companyId: string | undefined;
  entry: PettyCashEntry;
}) {
  const { mutate, isPending } = useRealizePettyCashEntryVat(companyId);
  if (entry.vatTreatment !== 'SUSPENSE' || entry.vatRealizedAt) return null;
  return (
    <Can permission="pettycash.create">
      <button
        type="button"
        className="btn sm ghost"
        data-testid={`pc-realize-vat-${entry.id}`}
        style={{ fontSize: 12, height: 22, padding: '0 6px' }}
        disabled={isPending}
        onClick={() => mutate(entry.id)}
        title="รับรู้ภาษีซื้อค้างรับ (โอน suspense → claimable)"
      >
        {isPending ? '…' : 'รับรู้ภาษีซื้อ'}
      </button>
    </Can>
  );
}
